// per DESIGN.md §1/§10 (composite extension contract) — the row overflow menu
// extracted from the aggregates table so crowded admin rows share one kebab
// instead of stacking six buttons per row.
import { Fragment, useEffect, useRef, useState } from 'react';
import type { ComponentRef, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { MudButton, MudIcon, MudMenu, MudMenuItem, MudSeparator } from '@egov-moldova/mud-react';
import { useAnchoredOverlay } from './anchored-overlay';
import { focusMenuItemWhenReady } from './focus-menu-item';

export interface RowAction {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Why the action is unavailable. Rendered under the label, and as the title,
   *  only while `disabled` — the wireframe's `motiv`
   *  (wireframes/react/src/components/ActiuniMenu.tsx:16,:159,:168). A disabled
   *  entry that does not say why is a refusal with no reason (Constitution IV). */
  reason?: string;
  /** Draw a rule above this entry, grouping what follows apart from what came
   *  before — the wireframe's `sep`. */
  separator?: boolean;
}

export interface RowActionsMenuProps {
  label: string;
  actions: RowAction[];
  /** `sm` in a table row, `md` beside a page header's own buttons — the
   *  wireframe uses the same menu in both places (ActiuniMenu.tsx:26, used at
   *  AdminAgregari.tsx:1494 with size="md"). */
  size?: 'sm' | 'md';
  /** Which edge of the menu lines up with the trigger.
   *
   *  `start` — the default, and what every table here needs: the kebab is the
   *  FIRST column (`leading-actions-table`, the wireframe's own arrangement), so
   *  the menu opens rightwards into the row it acts on. Measured on the
   *  wireframe: trigger left 336, menu left 336.
   *
   *  `end` for a trigger that sits at the right of its container — a page
   *  header's actions — where opening rightwards would run off the viewport. */
  align?: 'start' | 'end';
}

export function RowActionsMenu({
  label,
  actions,
  size = 'sm',
  align = 'start',
}: RowActionsMenuProps): JSX.Element {
  // The menu is portaled to <body> because the admin table cards clip overflow
  // (border-radius), which would cut the menu off. Placement belongs to the
  // shared anchored-overlay helper, not to this component: it flips above the
  // trigger when the row sits near the fold, clamps to every viewport edge, caps
  // the height only when the viewport leaves no room, and repositions while the
  // page scrolls instead of closing — spec 002 contracts/shared-fixes.md §2.
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<ComponentRef<typeof MudMenu> | null>(null);
  // This was pinned to `end` for every caller, which lined the menu's RIGHT edge
  // up with the kebab's — so a menu hanging off the leading column opened
  // leftwards, away from the row, and over the navigation rail. It was merely
  // cramped at the old 180px width; at MUD's published 270 it is plainly wrong.
  const { style, placement } = useAnchoredOverlay({
    open,
    triggerRef: rootRef,
    overlayRef: menuRef,
    align,
  });

  // mud-menu owns the roving focus (arrows, Home, End, Escape) but not the first
  // landing, and cannot take focus before it has rendered: the effect below runs
  // in the commit that mounts it, so a bare `focus()` here was a no-op and left
  // the focus on the kebab (059 V-093). The helper waits for the menu.
  useEffect(() => {
    if (!open) return undefined;
    return focusMenuItemWhenReady({ menu: menuRef.current });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function handleClick(event: MouseEvent): void {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  function closeAndRefocus(): void {
    setOpen(false);
    rootRef.current?.querySelector('mud-button')?.focus();
  }

  // mud-menu moves focus on the arrows, Home and End by itself, and on Escape it
  // emits `mudClose` (closeAndRefocus above). What stays here is keeping that
  // Escape from also reaching a dialog this portal is a React child of.
  //
  // Tab is the other way out: the menu is portaled to the end of <body>, so a Tab
  // from inside it would walk on past the page's last control and leave the menu
  // open behind the reader (059 V-093). It closes the menu and hands the focus
  // back to the trigger *before* the browser moves it, so the Tab then continues
  // from the row the reader was on, as it does after any popup menu.
  function handleMenuKeyDown(event: ReactKeyboardEvent): void {
    if (event.key === 'Escape') event.stopPropagation();
    if (event.key === 'Tab') closeAndRefocus();
  }

  function toggle(): void {
    setOpen((previous) => !previous);
  }

  return (
    <div className="simsm-row-actions" ref={rootRef}>
      {/* A bare glyph, not a filled chip. The wireframe's trigger is
          `btn text strict icon-only` (wireframes/react/src/components/ActiuniMenu.tsx:124),
          transparent at rest — a filled square on every row of every register put a
          column of grey chips down the edge of each table. */}
      <MudButton
        shape="circular"
        appearance="text"
        variant="strict"
        size={size}
        iconOnly
        label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <MudIcon slot="icon" name="more-horizontal" size={size === 'md' ? 20 : 16} />
      </MudButton>
      {open
        ? createPortal(
            <MudMenu
              className="simsm-row-actions__menu"
              type="contextual"
              aria-label={label}
              ref={menuRef}
              style={style}
              // Placement is asserted by the e2e geometry check, and a flipped
              // menu may want different chrome later.
              data-placement={placement ?? undefined}
              onKeyDown={handleMenuKeyDown}
              onMudSelect={(event) => {
                const action = actions[Number(event.detail.value)];
                if (!action) return;
                // The focus goes back to the kebab before the action runs. An
                // action that opens a MudModal has it store the element that had
                // the focus, to restore on close; left on the row, that element
                // was gone with the menu, and the focus fell to <body>.
                closeAndRefocus();
                action.onSelect();
              }}
              onMudClose={closeAndRefocus}
            >
              {actions.map((action, index) => {
                const reason = action.disabled ? action.reason : undefined;
                return (
                  <Fragment key={`${index}-${action.label}`}>
                    {/* The wireframe's `sep` (ActiuniMenu.tsx:153): a rule that
                        groups the destructive tail apart from the head. */}
                    {action.separator ? <MudSeparator inset /> : null}
                    {/* The label and, while disabled, the sentence saying why
                        (the wireframe's `motiv`, ActiuniMenu.tsx:168) share the
                        item's default slot: mud-menu-item has no second one. */}
                    <MudMenuItem
                      className={
                        action.danger
                          ? 'simsm-row-actions__item simsm-row-actions__item--danger'
                          : 'simsm-row-actions__item'
                      }
                      value={String(index)}
                      disabled={action.disabled ?? false}
                      title={reason}
                    >
                      <span className="simsm-row-actions__item-text">
                        <span className="simsm-row-actions__item-label">{action.label}</span>
                        {reason ? (
                          <span className="simsm-row-actions__item-reason">{reason}</span>
                        ) : null}
                      </span>
                    </MudMenuItem>
                  </Fragment>
                );
              })}
            </MudMenu>,
            document.body,
          )
        : null}
    </div>
  );
}
